import { buildPostingTimeViewContext } from './optimalPostingTimesUtils'

const FULL_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const CARD_LABELS = {
  best: 'BEST OPPORTUNITY',
  consistent: 'MOST RELIABLE',
  consistentFallback: 'MOST RELIABLE',
  improvement: 'NEEDS REVIEW',
}

const UNAVAILABLE_MESSAGE = 'Not enough publishing data to identify this opportunity.'
export const OPPORTUNITIES_EMPTY_MESSAGE =
  'Not enough published-post data is available to identify notable publishing windows for the selected period.'

function getActivityLevel(val, samples, quartiles, minSamples = 1) {
  if (!samples || samples < minSamples) return 0
  const v = val ?? 0
  if (quartiles.q1 <= 0 && quartiles.q3 <= 0) return v > 0 ? 2 : 0
  if (v <= quartiles.q1) return 1
  if (v < quartiles.q3) return 2
  return 3
}

function formatHourLabel(hour) {
  const h = Math.max(0, Math.min(23, Math.floor(hour)))
  const isPM = h >= 12
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${displayHour}:00 ${isPM ? 'PM' : 'AM'}`
}

function formatHourRangeLabel(startHour, endHour) {
  if (startHour === endHour) return formatHourLabel(startHour)
  const startPM = startHour >= 12
  const endPM = endHour >= 12
  if (startPM === endPM) {
    const startDisplay = startHour === 0 ? 12 : startHour > 12 ? startHour - 12 : startHour
    const endDisplay = endHour === 0 ? 12 : endHour > 12 ? endHour - 12 : endHour
    return `${startDisplay}:00-${endDisplay}:00 ${startPM ? 'PM' : 'AM'}`
  }
  return `${formatHourLabel(startHour)}-${formatHourLabel(endHour)}`
}

function formatWeekdayWindow(dayIndex, startHour, endHour) {
  const dayName = FULL_DAY_NAMES[dayIndex] ?? 'Day'
  return `${dayName} - ${formatHourRangeLabel(startHour, endHour)}`
}

function parseISODate(iso) {
  if (!iso || typeof iso !== 'string') return null
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

function formatDateShort(iso) {
  const dt = parseISODate(iso)
  if (!dt) return iso
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatHourLabelRange(startLabel, endLabel) {
  if (startLabel === endLabel) return startLabel
  const parse = (label) => {
    const match = String(label).match(/^(\d{1,2})(?::00)?\s*(AM|PM)$/i)
      || String(label).match(/^(\d{1,2})(AM|PM)$/i)
    if (!match) return null
    return { hour: Number(match[1]), period: match[2].toUpperCase() }
  }
  const start = parse(startLabel)
  const end = parse(endLabel)
  if (start && end && start.period === end.period) {
    return `${start.hour}:00-${end.hour}:00 ${start.period}`
  }
  return `${startLabel}-${endLabel}`
}

function formatDateWindow(dateIso, startHourLabel, endHourLabel) {
  return `${formatDateShort(dateIso)} - ${formatHourLabelRange(startHourLabel, endHourLabel)}`
}

function weightedEngagement(slots) {
  const totalPosts = slots.reduce((sum, slot) => sum + slot.posts, 0)
  if (totalPosts <= 0) return 0
  const weighted = slots.reduce((sum, slot) => sum + slot.engagement * slot.posts, 0)
  return weighted / totalPosts
}

function getWeekdaySlotPerformance(matrix, slot) {
  return Number(matrix?.[slot.dayIndex]?.[slot.hour] ?? 0)
}

function getDateSlotPerformance(matrix, slot) {
  return Number(matrix?.[slot.row]?.[slot.col] ?? 0)
}

function windowShareOfTotalEngagement(window, engagements, totalEngagements, getSlotEngagement) {
  if (
    totalEngagements == null ||
    !Number.isFinite(Number(totalEngagements)) ||
    Number(totalEngagements) <= 0 ||
    !engagements
  ) {
    return null
  }

  let sum = 0
  for (const slot of window.slots || []) {
    const value = getSlotEngagement(engagements, slot)
    if (value == null || !Number.isFinite(Number(value))) return null
    sum += Number(value)
  }
  return (sum / Number(totalEngagements)) * 100
}

function enrichWindowWithMetrics(window, matrix, samples, viewContext, {
  isHighestPerforming = false,
  getPerformance,
  engagements = null,
  totalEngagements = null,
  getSlotEngagement,
}) {
  const postCount = window.postCount ?? window.slots.reduce((sum, slot) => sum + slot.posts, 0)
  if (postCount < 1) {
    return {
      ...window,
      postCount,
      performanceVsPeak: null,
      shareOfTotalEngagement: null,
      isHighestPerforming: false,
      hasValidPosts: false,
    }
  }

  const slotsWithPerformance = window.slots.map((slot) => ({
    ...slot,
    engagement: getPerformance(matrix, slot),
  }))
  const windowPerformance = weightedEngagement(slotsWithPerformance)

  const { peakPerformance } = viewContext
  const shareOfTotalEngagement = windowShareOfTotalEngagement(
    window,
    engagements,
    totalEngagements,
    getSlotEngagement,
  )

  let performanceVsPeak = null
  if (!isHighestPerforming && peakPerformance != null && peakPerformance > 0) {
    performanceVsPeak = (windowPerformance / peakPerformance) * 100
  }

  return {
    ...window,
    postCount,
    performanceVsPeak,
    shareOfTotalEngagement,
    isHighestPerforming,
    hasValidPosts: true,
  }
}

function attachOpportunityMetrics(windows, matrix, samples, minSamples, getPerformance, {
  engagements = null,
  totalEngagements = null,
  getSlotEngagement,
} = {}) {
  const viewContext = buildPostingTimeViewContext(matrix, samples, { minSamples })
  if (!windows.length) return { viewContext, windows: [] }

  const ranked = [...windows].sort(compareWindows)
  const bestKey = ranked[0]?.key

  return {
    viewContext,
    windows: windows.map((window) =>
      enrichWindowWithMetrics(window, matrix, samples, viewContext, {
        isHighestPerforming: window.key === bestKey,
        getPerformance,
        engagements,
        totalEngagements,
        getSlotEngagement,
      }),
    ),
  }
}

function buildWindows(groupedSlots, formatWindowLabel) {
  const windows = []

  Object.values(groupedSlots).forEach((daySlots) => {
    const sorted = [...daySlots].sort((a, b) => a.hour - b.hour)
    let current = null

    const flush = () => {
      if (!current) return
      windows.push({
        key: current.key,
        timeWindow: current.timeWindow,
        avgEngagement: weightedEngagement(current.slots),
        postCount: current.slots.reduce((sum, slot) => sum + slot.posts, 0),
        tier: current.tier,
        slots: current.slots,
      })
      current = null
    }

    sorted.forEach((slot) => {
      const canExtend =
        current &&
        current.tier === slot.tier &&
        slot.hour === current.endHour + 1

      if (canExtend) {
        current.endHour = slot.hour
        current.slots.push(slot)
        current.key = `${slot.groupKey}-${current.startHour}-${current.endHour}`
        current.timeWindow = formatWindowLabel(current)
        return
      }

      flush()
      current = {
        tier: slot.tier,
        dayIndex: slot.dayIndex,
        dateIso: slot.dateIso,
        startHour: slot.hour,
        endHour: slot.hour,
        slots: [slot],
        key: `${slot.groupKey}-${slot.hour}-${slot.hour}`,
        timeWindow: formatWindowLabel({
          dayIndex: slot.dayIndex,
          dateIso: slot.dateIso,
          startHour: slot.hour,
          endHour: slot.hour,
          slots: [slot],
        }),
      }
    })

    flush()
  })

  return windows
}

function compareWindows(a, b) {
  if (b.avgEngagement !== a.avgEngagement) return b.avgEngagement - a.avgEngagement
  if (b.postCount !== a.postCount) return b.postCount - a.postCount
  return String(a.key).localeCompare(String(b.key))
}

function compareWindowsLowEngagement(a, b) {
  if (a.avgEngagement !== b.avgEngagement) return a.avgEngagement - b.avgEngagement
  if (b.postCount !== a.postCount) return b.postCount - a.postCount
  return String(a.key).localeCompare(String(b.key))
}

function globalMeanEngagement(slots) {
  return weightedEngagement(slots)
}

function pickBestPerforming(windows) {
  if (!windows.length) return null
  return [...windows].sort(compareWindows)[0]
}

function pickConsistentPerformer(windows, globalMean, excludedKeys) {
  const available = windows.filter((window) => !excludedKeys.has(window.key))
  if (!available.length) return null

  const aboveAverage = available.filter(
    (window) => window.avgEngagement >= globalMean && window.tier >= 2,
  )

  if (aboveAverage.length) {
    const ranked = [...aboveAverage].sort((a, b) => {
      const scoreA = a.avgEngagement * Math.sqrt(a.postCount)
      const scoreB = b.avgEngagement * Math.sqrt(b.postCount)
      if (scoreB !== scoreA) return scoreB - scoreA
      return compareWindows(a, b)
    })
    return {
      cardLabel: CARD_LABELS.consistent,
      ...ranked[0],
    }
  }

  const highTier = available
    .filter((window) => window.tier >= 2)
    .sort((a, b) => {
      if (b.postCount !== a.postCount) return b.postCount - a.postCount
      return compareWindows(a, b)
    })

  if (!highTier.length) return null

  return {
    cardLabel: CARD_LABELS.consistentFallback,
    ...highTier[0],
  }
}

function pickImprovementOpportunity(windows, globalMean, excludedKeys) {
  const available = windows.filter((window) => !excludedKeys.has(window.key))
  if (!available.length) return null

  const candidates = available
    .filter((window) => window.tier === 1 || window.avgEngagement < globalMean)
    .filter((window) => window.postCount >= 1)
    .sort(compareWindowsLowEngagement)

  if (!candidates.length) return null

  return {
    cardLabel: CARD_LABELS.improvement,
    ...candidates[0],
  }
}

function weekdaySlotEngagement(engagements, slot) {
  return engagements?.[slot.dayIndex]?.[slot.hour]
}

function dateSlotEngagement(engagements, slot) {
  return engagements?.[slot.row]?.[slot.col]
}

function toOpportunityResult(best, consistent, improvement, matrix, samples, minSamples, getPerformance, {
  engagements = null,
  totalEngagements = null,
  getSlotEngagement,
} = {}) {
  const windows = [best, consistent, improvement].filter(Boolean)
  const { windows: enrichedWindows } = attachOpportunityMetrics(
    windows,
    matrix,
    samples,
    minSamples,
    getPerformance,
    { engagements, totalEngagements, getSlotEngagement },
  )
  const enrichedByKey = Object.fromEntries(enrichedWindows.map((window) => [window.key, window]))

  const enrich = (window) => (window ? enrichedByKey[window.key] ?? window : null)

  return {
    bestPerforming: enrich(best ? { cardLabel: CARD_LABELS.best, ...best } : null),
    consistentPerformer: enrich(consistent),
    improvementOpportunity: enrich(improvement),
    hasAny: Boolean(best || consistent || improvement),
  }
}

function collectWeekdaySlots(matrix, samples, quartiles, minSamples) {
  const slots = []
  for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
    for (let hour = 0; hour < 24; hour += 1) {
      const engagement = Number(matrix?.[dayIndex]?.[hour] ?? 0)
      const posts = Number(samples?.[dayIndex]?.[hour] ?? 0)
      if (posts < minSamples) continue
      const tier = getActivityLevel(engagement, posts, quartiles, minSamples)
      if (tier === 0) continue
      slots.push({
        dayIndex,
        hour,
        engagement,
        posts,
        tier,
        windowKey: `weekday-${dayIndex}-${hour}`,
        groupKey: String(dayIndex),
      })
    }
  }
  return slots
}

function collectDateSlots(matrix, samples, quartiles, dates, hourList, minSamples) {
  const slots = []
  for (let row = 0; row < matrix.length; row += 1) {
    for (let col = 0; col < (matrix[row]?.length ?? 0); col += 1) {
      const engagement = Number(matrix[row]?.[col] ?? 0)
      const posts = Number(samples[row]?.[col] ?? 0)
      if (posts < minSamples) continue
      const tier = getActivityLevel(engagement, posts, quartiles, minSamples)
      if (tier === 0) continue
      const dateIso = dates[col]
      const hourLabel = hourList[row] ?? String(row)
      slots.push({
        row,
        col,
        hour: row,
        hourLabel,
        dateIso,
        engagement,
        posts,
        tier,
        windowKey: `date-${dateIso}-${row}`,
        groupKey: dateIso,
      })
    }
  }
  return slots
}

function groupSlotsByDay(slots) {
  return slots.reduce((acc, slot) => {
    if (!acc[slot.groupKey]) acc[slot.groupKey] = []
    acc[slot.groupKey].push(slot)
    return acc
  }, {})
}

export function deriveWeekdayPostingOpportunities(matrix, samples, quartiles, {
  minSamples = 1,
  engagements = null,
  totalEngagements = null,
} = {}) {
  const metricOptions = { engagements, totalEngagements, getSlotEngagement: weekdaySlotEngagement }
  const slots = collectWeekdaySlots(matrix, samples, quartiles, minSamples)
  if (!slots.length) {
    return toOpportunityResult(null, null, null, matrix, samples, minSamples, getWeekdaySlotPerformance, metricOptions)
  }

  const grouped = groupSlotsByDay(slots)
  const windows = buildWindows(grouped, (window) =>
    formatWeekdayWindow(
      window.dayIndex ?? window.slots[0].dayIndex,
      window.startHour,
      window.endHour,
    ),
  )

  if (!windows.length) {
    return toOpportunityResult(null, null, null, matrix, samples, minSamples, getWeekdaySlotPerformance, metricOptions)
  }

  const mean = globalMeanEngagement(slots)
  const best = pickBestPerforming(windows)
  const excluded = new Set(best ? [best.key] : [])
  const consistent = pickConsistentPerformer(windows, mean, excluded)
  if (consistent) excluded.add(consistent.key)
  const improvement = pickImprovementOpportunity(windows, mean, excluded)

  return toOpportunityResult(
    best,
    consistent,
    improvement,
    matrix,
    samples,
    minSamples,
    getWeekdaySlotPerformance,
    metricOptions,
  )
}

export function deriveDateRangePostingOpportunities(
  matrix,
  samples,
  quartiles,
  dates,
  hourList,
  { minSamples = 1, engagements = null, totalEngagements = null } = {},
) {
  const metricOptions = { engagements, totalEngagements, getSlotEngagement: dateSlotEngagement }
  const slots = collectDateSlots(matrix, samples, quartiles, dates, hourList, minSamples)
  if (!slots.length) {
    return toOpportunityResult(null, null, null, matrix, samples, minSamples, getDateSlotPerformance, metricOptions)
  }

  const grouped = groupSlotsByDay(slots)
  const windows = buildWindows(grouped, (window) => {
    const dateIso = window.dateIso ?? window.slots[0].dateIso
    const startLabel = hourList[window.startHour] ?? hourList[window.slots[0].hour]
    const endLabel = hourList[window.endHour] ?? hourList[window.slots[window.slots.length - 1].hour]
    return formatDateWindow(dateIso, startLabel, endLabel)
  })

  if (!windows.length) {
    return toOpportunityResult(null, null, null, matrix, samples, minSamples, getDateSlotPerformance, metricOptions)
  }

  const mean = globalMeanEngagement(slots)
  const best = pickBestPerforming(windows)
  const excluded = new Set(best ? [best.key] : [])
  const consistent = pickConsistentPerformer(windows, mean, excluded)
  if (consistent) excluded.add(consistent.key)
  const improvement = pickImprovementOpportunity(windows, mean, excluded)

  return toOpportunityResult(
    best,
    consistent,
    improvement,
    matrix,
    samples,
    minSamples,
    getDateSlotPerformance,
    metricOptions,
  )
}

export { UNAVAILABLE_MESSAGE }
